/**
 * workspaceModel — 강의 작업 화면의 상태 판정(순수 함수). 설계 FE-20260912 §4.
 *
 * 세 가지를 따로 쥔다:
 *   - 서버 원본(serverBase) — 마지막으로 받은 강의 헤더·줄·revision
 *   - 줄별 조건 초안(drafts) — 사용자가 고친 식. 줄을 옮겨 다니거나 서버를 다시 읽어도 버리지 않는다
 *   - 검색 결과(results) — 실제로 보낸 식(열쇠)과 그 응답
 * 「저장 필요」는 초안과 원본의 차이, 「재검색 필요」는 지금 식과 마지막 검색 식의 차이다 — 둘은 따로 성립한다.
 * 이름·상위 줄은 식에 들어가지 않으므로 검색 비교에서 빠진다.
 *
 * 쓰기 응답은 모두 revision 을 싣는다(백엔드 api/v2/syllabus.py — P4·P7 헤더, N1·N2 줄+revision, N3 지운 id+revision,
 * N4 순서+revision). 그래서 쓰기마다 강의 전체를 다시 읽지 않고 응답을 목록에 반영한다(apply*).
 */
import { inspectExpr } from './syllabusQuery';

export const sameJson = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * 식의 비교 열쇠 — 검색 규칙으로 정규화가 되면 정규화된 식으로(값 순서·중복이 달라도 같은 식), 안 되면 원본 그대로.
 * null(제목 줄)은 'null'.
 */
export function exprKey(expr) {
  if (expr == null) return 'null';
  const r = inspectExpr(expr, { mode: 'search' });
  return JSON.stringify(r.ok ? r.normalized : expr);
}

/** 초안이 원본과 다른가(저장 필요) */
export const isQueryDirty = (draft, server) => exprKey(draft) !== exprKey(server);

/**
 * 결과가 지금 식·필터·페이지 기준과 맞는가.
 *   none  — 검색한 적 없음
 *   fresh — 마지막 성공 검색이 지금 식으로 한 것
 *   stale — 식이 바뀌어 아래 결과는 이전 검색의 것
 */
export function freshness(result, currentKey) {
  if (!result || !result.data) return 'none';
  return result.key === currentKey ? 'fresh' : 'stale';
}

// ── 쓰기 응답을 줄 목록에 반영 ──

const nodeFields = (n) => ({
  node_id: n.node_id,
  node_uuid: n.node_uuid,
  parent_id: n.parent_id ?? null,
  depth: n.depth,
  order_no: n.order_no,
  name: n.name,
  query: n.query ?? null,
  updated_at: n.updated_at,
});

/** N1 — 새 줄을 넣는다(형제 끝 순번은 응답의 order_no 그대로) */
export function applyCreated(items, node) {
  const list = (items || []).filter((it) => String(it.node_id) !== String(node.node_id));
  return [...list, nodeFields(node)];
}

/** N2 — 그 줄을 응답으로 바꾼다(부모를 바꿨으면 서버가 형제 끝으로 보냈다 — order_no 가 그것을 말한다) */
export function applyUpdated(items, node) {
  return (items || []).map((it) => (String(it.node_id) === String(node.node_id) ? { ...it, ...nodeFields(node) } : it));
}

/** N3 — 지운 줄(하위 포함)을 뺀다 */
export function applyDeleted(items, ids) {
  const gone = new Set((ids || []).map(String));
  return (items || []).filter((it) => !gone.has(String(it.node_id)));
}

/** N4 — 그 부모의 자식 순서를 0..n-1 로 다시 매긴다(서버와 같은 재부여) */
export function applyReordered(items, ids) {
  const pos = new Map((ids || []).map((id, k) => [String(id), k]));
  return (items || []).map((it) => (pos.has(String(it.node_id)) ? { ...it, order_no: pos.get(String(it.node_id)) } : it));
}

/**
 * 새 줄을 선택 줄 바로 다음에 두려면 보낼 형제 순서 — 새 줄은 N1 이 형제 끝에 만들었다.
 * afterId 가 없거나 형제가 아니면 null(순서를 바꿀 필요 없음 · 끝 그대로).
 */
export function orderWithInsertAfter(siblingIds, newId, afterId) {
  const list = (siblingIds || []).map(String).filter((id) => id !== String(newId));
  if (afterId == null) return null;
  const k = list.indexOf(String(afterId));
  if (k < 0 || k === list.length - 1) return null;
  return [...list.slice(0, k + 1), String(newId), ...list.slice(k + 1)];
}
